import { useCallback } from 'react';
import { mutate } from 'swr';
import type { InteractionTypeName } from '@/common/types/interaction';
import { matchCoreKey } from '$api-clients/hooks';

/**
 * Revalidates every core list that can be showing an interaction of `type`.
 *
 * A row reaches the screen through more than one key: the transactions list view
 * reads `/listings` and `/offers` directly but `/interactions` for leases, and
 * Home's needs-attention listing agreements read `/listings`. Mutating only one
 * leaves the other stale.
 *
 * Uses the global `mutate` so module-scope callers (chat tool definitions) can
 * use it too; the bare call revalidates while keeping cached data in place.
 */
export async function mutateInteractionLists(type: InteractionTypeName) {
  const revalidations = [mutate(matchCoreKey('/interactions'))];
  switch (type) {
    case 'listing':
      revalidations.push(mutate(matchCoreKey('/listings')));
      break;
    case 'offer':
      revalidations.push(mutate(matchCoreKey('/offers')));
      break;
    default:
      break;
  }

  await Promise.all(revalidations);
}

export function useMutateInteractionLists() {
  return useCallback(
    async (type: InteractionTypeName) => mutateInteractionLists(type),
    [],
  );
}
